import React from 'react';
import get from 'lodash.get';
import { useSelector } from 'react-redux';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Box, Button, FlexBox, Grid, MuiBox, Typography } from '@kis-technologies/kis-ui';
import { Scrollbar } from 'components/Scrollbar';
import { selectors as cartSelectors, Cart } from 'services/cart';
import { selectors as configSelectors, Configuration, Message } from 'services/configuration';
import { selectors as ticketingSelectors } from 'services/ticketing';
import { useStyles } from '../styled';
import { LayoutProps } from '../types';

export const Layout = (props: LayoutProps) => {
  const { t, i18n } = useTranslation();
  const { headerText, subHeaderText, hasTickets, refreshModel, children } = props;

  const navigate = useNavigate();
  const location = useLocation();
  const cart = useSelector(cartSelectors.selectCart) as Cart;
  const configuration = useSelector(configSelectors.selectConfiguration) as Configuration;
  const promoApplied = useSelector(ticketingSelectors.selectAppliedPriceTypePromo);
  const ticketingPath = useSelector(ticketingSelectors.selectTicketingConfigPath);

  const promoEnabled: boolean = get(configuration, `${ticketingPath}.promoCode.priceType.enabled`, false);
  const promoMessages: Message = get(configuration, `${ticketingPath}.promoCode.priceType.messages`, {});
  const promoSelectionEnabled: boolean = get(configuration, `${ticketingPath}.promoCode.selection.enabled`, false);
  const promoEnterButton = get(promoMessages, `buttons.${i18n.language}[0]`, t('promoCode.enterButton'));
  const fontFamily = get(configuration, 'configuration.globalSettings.fonts.primary');

  const classes = useStyles({ fontFamily, hasPromo: promoEnabled })();

  const getPromoCodePath = () => {
    let path = '/ticketing/price-type/promo';
    if (promoSelectionEnabled) {
      path = '/ticketing/promo-code/selection';
    }
    return path;
  };

  return (
    <Grid container className={classes.tierSelectionContainer}>
      <Grid container item className={classes.tierSelectInnerContainer}>
        {/* Header block */}
        <Grid container className={classes.tierHeaderOuterBlock}>
          <MuiBox className={classes.tierHeaderBlock}>
            {/* Title */}
            <Box>
              <Typography className={classes.ticketHeader}>{headerText}</Typography>
            </Box>
            <MuiBox className={classes.ticketSubHeading}>
              <Typography fontSize={30}>{subHeaderText}</Typography>
            </MuiBox>
          </MuiBox>
          {promoEnabled && !promoApplied && !cart && (
            <FlexBox align="start" padding="3 0 0 0">
              <Button
                customSize="x-large"
                appearance="white"
                onClick={() => navigate(getPromoCodePath(), { state: { from: location.pathname } })}
                data-testid="promocode-button"
              >
                {promoEnterButton}
              </Button>
            </FlexBox>
          )}
        </Grid>
        {/* content block */}
        {hasTickets ? (
          <Grid container direction="column" className={classes.contentBlock}>
            <FlexBox sx={{ minHeight: '26.5rem' }} direction="column" gutter={3} noWrap flexOverride="1">
              <Scrollbar elToFocus={refreshModel}>
                <FlexBox direction="column" gutter={3} noWrap flexOverride="1">
                  {children}
                </FlexBox>
              </Scrollbar>
            </FlexBox>
          </Grid>
        ) : (
          <Grid container direction="column" className={classes.contentBlock}>
            <FlexBox justify="center" align="center" height="400px">
              <Typography align="center" fontSize={40} fontWeight={900}>
                {t('tierSelection.ticketsNotAvailable')}
              </Typography>
            </FlexBox>
          </Grid>
        )}
      </Grid>
    </Grid>
  );
};
